import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  Close,
  Warning,
  Delete,
} from '@mui/icons-material';
import { Lead } from '../types';

interface DeleteLeadModalProps {
  open: boolean;
  lead: Lead | null;
  onClose: () => void;
  onConfirm: () => void;
  isDeleting?: boolean;
  isMobile?: boolean;
}

const DeleteLeadModal: React.FC<DeleteLeadModalProps> = ({
  open,
  lead,
  onClose,
  onConfirm,
  isDeleting = false,
  isMobile
}) => {
  if (!lead) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: isMobile ? 0 : 2,
          margin: isMobile ? 0 : '24px',
          maxWidth: isMobile ? '100vw' : '500px',
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'linear-gradient(135deg, #d32f2f 0%, #f44336 100%)',
          color: 'white',
          p: 2,
          minHeight: '60px'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning sx={{ fontSize: 24 }} />
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            Confirmar Exclusão
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          disabled={isDeleting}
          sx={{
            color: 'white',
            backgroundColor: 'rgba(255,255,255,0.2)',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' }
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 3, mt: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Atenção! Esta ação não pode ser desfeita.
          </Typography>
          <Typography variant="body2">
            O lead será permanentemente removido do sistema, incluindo todas as observações e histórico associados.
          </Typography>
        </Alert>

        <Box sx={{ 
          p: 2, 
          backgroundColor: 'rgba(0,0,0,0.05)', 
          borderRadius: 1,
          border: '1px solid rgba(0,0,0,0.1)',
          mb: 2
        }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
            Lead que será excluído:
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
            <strong>Nome:</strong> {lead.name}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
            <strong>Telefone:</strong> {lead.phoneNumber}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
            <strong>Email:</strong> {lead.email}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            <strong>Status:</strong> {lead.status || 'N/A'}
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary">
          Tem certeza de que deseja excluir este lead? Esta ação é irreversível.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: 2, backgroundColor: 'background.paper', borderTop: (theme) => `1px solid ${theme.palette.divider}` }}>
        <Button 
          onClick={onClose} 
          color="inherit" 
          disabled={isDeleting}
          sx={{ mr: 1 }}
        >
          Cancelar
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="error"
          startIcon={isDeleting ? <CircularProgress size={16} /> : <Delete />}
          disabled={isDeleting}
          sx={{
            background: 'linear-gradient(135deg, #d32f2f 0%, #f44336 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #b71c1c 0%, #d32f2f 100%)',
            }
          }}
        >
          {isDeleting ? 'Excluindo...' : 'Excluir Lead'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteLeadModal;
